import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchBookSummary,
  fetchCategories,
  fetchLanguages,
  fetchPeople,
  fetchPeopleRoles,
} from "@/data/mockData";
import { fetchPeopleByRole } from "@/api/people";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BookSummary,
  BookAuthor,
  Category,
  Language,
  Chapter,
  Person,
  SummaryContent,
} from "@/types";
import {
  ArrowLeft,
  Save,
  Trash,
  Globe,
  Plus,
  Headphones,
  GripVertical,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Bot,
  FileText,
  Loader2,
  Sparkles,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import ChapterEditor from "@/components/books/ChapterEditor";
import AudioUploader from "@/components/books/AudioUploader";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ShortDescriptionEditor from "@/components/books/ShortDescriptionEditor";
import React from "react";
import { saveChapter } from "@/api/books";
import { MediaPicker } from "@/components/ui";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

interface SortableLanguageItemProps {
  language: Language;
  hasAudio: boolean;
  contentType?: 'summary' | 'full_book';
  onDelete: (code: string) => void;
  isSelected: boolean;
  onSelect: (code: string) => void;
}

const SortableLanguageItem = ({
  language,
  hasAudio,
  contentType,
  onDelete,
  isSelected,
  onSelect,
}: SortableLanguageItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: language.code,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <div
        className="cursor-grab active:cursor-grabbing p-1 -ml-1 hover:bg-accent rounded-md"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
      </div>
      <Button
        variant={isSelected ? "default" : "outline"}
        size="sm"
        className="flex items-center gap-2"
        onClick={() => onSelect(language.code)}
      >
        <Globe className="h-4 w-4" />
        {language.name}
        {contentType === "full_book" && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-primary/20 text-primary font-medium">
            Full
          </span>
        )}
        {hasAudio && <Headphones className="h-3 w-3" />}
        <Button
          variant="ghost"
          size="sm"
          className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(language.code);
          }}
        >
          <Trash className="h-3 w-3" />
        </Button>
      </Button>
    </div>
  );
};

// Update the ExtendedBookSummary type to remove the chapters property
// This is a temporary solution to fix the immediate error while preserving TypeScript safety
type ExtendedBookSummary = BookSummary & {
  // Remove the chapters property as it's now in summaryContents
};

const defaultBook: ExtendedBookSummary = {
  id: 0,
  title: "",
  authors: [],
  coverImage: "",
  coverBgColor: "#E9ECEF",
  categories: [],
  readingTime: 0,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  rating: 0,
  viewCount: 0,
  summaryContents: [],
  isActive: false, // default to unpublished
  isFree: false, // default to premium
};

const BookForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isNewBook = id === "new" || window.location.pathname.includes("/new");

  // Use the extended type for the book state
  const [book, setBook] = useState<ExtendedBookSummary>(
    isNewBook ? defaultBook : { ...defaultBook }
  );
  const [currentLanguage, setCurrentLanguage] = useState<string>("");
  const [languageToDelete, setLanguageToDelete] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [bookIdToDelete, setBookIdToDelete] = useState<number | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isGeneratingEmbeddings, setIsGeneratingEmbeddings] = useState(false);
  const [embeddingCounts, setEmbeddingCounts] = useState<Record<string, number>>({});
  const [aiContextStatus, setAiContextStatus] = useState<Record<string, { context: "up_to_date" | "outdated" | "missing"; embeddings: "up_to_date" | "outdated" | "missing" }>>({});

  const { data: fetchedBook, isLoading: isLoadingBook } = useQuery({
    queryKey: ["book", id],
    queryFn: async () => {
      try {
        console.log("Fetching book with ID:", id);
        const result = await fetchBookSummary(Number(id) || 0);
        if (!result && !isNewBook) {
          setError("Book not found or could not be loaded");
          console.error("Book not found:", id);
        }
        return result;
      } catch (err) {
        setError("Error loading book data");
        console.error("Error fetching book:", err);
        return null;
      }
    },
    enabled: !isNewBook && !!id,
  });

  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: languages, isLoading: isLoadingLanguages } = useQuery({
    queryKey: ["languages"],
    queryFn: fetchLanguages,
  });

  const { data: peopleData } = useQuery({
    queryKey: ["people"],
    queryFn: () => fetchPeople(1, 100, {}),
  });

  const { data: roles } = useQuery({
    queryKey: ["peopleRoles"],
    queryFn: fetchPeopleRoles,
  });

  // Find the Translator role ID
  const translatorRoleId = React.useMemo(() => {
    return roles?.find((role) => role.name === "Translator")?.id;
  }, [roles]);

  // Fetch translators specifically using fetchPeopleByRole
  const { data: translators } = useQuery({
    queryKey: ["translators", translatorRoleId],
    queryFn: () => {
      if (!translatorRoleId) return Promise.resolve([]);
      return fetchPeopleByRole(translatorRoleId);
    },
    enabled: !!translatorRoleId,
  });

  const authorsList = React.useMemo(() => {
    if (!peopleData?.data || !roles) return [];

    const authorRoleId = roles.find((role) => role.name === "Author")?.id;
    if (!authorRoleId) return [];

    return peopleData.data.filter((person) =>
      person.roles.some((role) => role.id === authorRoleId)
    );
  }, [peopleData, roles]);

  useEffect(() => {
    if (fetchedBook && !isNewBook) {
      // Ensure we have valid data before setting state
      if (fetchedBook) {
        console.log("Setting book data:", fetchedBook);

        // Check if summary contents have chapters
        if (
          fetchedBook.summaryContents &&
          fetchedBook.summaryContents.length > 0
        ) {
          fetchedBook.summaryContents.forEach((content) => {
            console.log(
              `Language ${content.language} has ${
                content.chapters?.length || 0
              } chapters:`,
              content.chapters
            );
          });
        }

        setBook(fetchedBook);
        // Get first language from summary contents sorted by position
        if (
          fetchedBook.summaryContents &&
          fetchedBook.summaryContents.length > 0
        ) {
          const sortedContents = [...fetchedBook.summaryContents].sort(
            (a, b) => (a.position || 0) - (b.position || 0)
          );
          setCurrentLanguage(sortedContents[0].language);
        }
      }
    }
  }, [fetchedBook, isNewBook]);

  // Fetch embedding counts per language for this book
  const fetchEmbeddingCounts = useCallback(async () => {
    if (!book.id || book.id === 0) return;
    const { data } = await supabase
      .from("content_embeddings")
      .select("language")
      .eq("book_id", book.id);

    if (data) {
      const counts: Record<string, number> = {};
      data.forEach((row) => {
        counts[row.language] = (counts[row.language] || 0) + 1;
      });
      setEmbeddingCounts(counts);
    }
  }, [book.id]);

  const fetchAiContextStatus = useCallback(async () => {
    if (!book.id || book.id === 0) return;

    // Fetch summary_contents with timestamps
    const { data: scData } = await supabase
      .from("summary_contents")
      .select("id, language, ai_context, ai_context_updated_at, embeddings_updated_at")
      .eq("book_id", book.id);

    if (!scData) return;

    const scIds = scData.map((sc) => sc.id);
    // Fetch latest chapter updated_at per summary_content
    const chapterLatest: Record<number, string> = {};
    if (scIds.length > 0) {
      const { data: chapData } = await supabase
        .from("chapters")
        .select("summary_content_id, updated_at")
        .in("summary_content_id", scIds);
      if (chapData) {
        for (const ch of chapData) {
          if (ch.summary_content_id) {
            const prev = chapterLatest[ch.summary_content_id];
            if (!prev || ch.updated_at > prev) {
              chapterLatest[ch.summary_content_id] = ch.updated_at;
            }
          }
        }
      }
    }

    const statusMap: Record<string, { context: "up_to_date" | "outdated" | "missing"; embeddings: "up_to_date" | "outdated" | "missing" }> = {};
    for (const sc of scData) {
      const lang = sc.language || "en";
      const hasContext = !!(sc.ai_context && sc.ai_context.trim().length > 0);
      const contextTs = sc.ai_context_updated_at;
      const embTs = sc.embeddings_updated_at;
      const latestChapter = chapterLatest[sc.id];

      let contextStatus: "up_to_date" | "outdated" | "missing" = "missing";
      if (hasContext && contextTs) {
        contextStatus = latestChapter && latestChapter > contextTs ? "outdated" : "up_to_date";
      }

      let embStatus: "up_to_date" | "outdated" | "missing" = "missing";
      if (embTs) {
        embStatus = contextTs && contextTs > embTs ? "outdated" : "up_to_date";
      }

      statusMap[lang] = { context: contextStatus, embeddings: embStatus };
    }
    setAiContextStatus(statusMap);
  }, [book.id]);

  useEffect(() => {
    fetchEmbeddingCounts();
    fetchAiContextStatus();
  }, [book.id, fetchEmbeddingCounts, fetchAiContextStatus]);

  const handleGenerateEmbeddings = async (language?: string) => {
    if (!book.id || book.id === 0) {
      toast({
        title: "Save book first",
        description: "Please save the book before generating embeddings.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingEmbeddings(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: "Not authenticated", variant: "destructive" });
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/generate-embeddings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
          "apikey": anonKey,
        },
        body: JSON.stringify({ bookId: book.id, language }),
      });

      const result = await res.json();
      console.log("generate-embeddings response:", res.status, result);

      if (!res.ok) {
        throw new Error(result?.error || `Server error: ${res.status}`);
      }

      toast({
        title: "Embeddings generated",
        description: `${result.chunksProcessed} chunk(s) processed and embedded.`,
      });

      // Refresh counts and status
      await fetchEmbeddingCounts();
      await fetchAiContextStatus();
    } catch (error) {
      console.error("Error generating embeddings:", error);
      toast({
        title: "Embedding generation failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingEmbeddings(false);
    }
  };

  const handleInputChange = (
    field: keyof ExtendedBookSummary,
    value:
      | string
      | number
      | Category[]
      | Language[]
      | Chapter[]
      | Person[]
      | BookAuthor[]
      | SummaryContent[]
      | boolean
  ) => {
    if (field === "coverImage") {
      console.log("Setting coverImage to:", value);
    }
    setBook((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (categoryIds: number[]) => {
    if (!categories) return;

    const selectedCategories = categories.filter((category) =>
      categoryIds.includes(category.id)
    );

    handleInputChange("categories", selectedCategories);
  };

  const addLanguage = (languageCode: string) => {
    if (!languages) return;

    const languageToAdd = languages.find((lang) => lang.code === languageCode);
    if (!languageToAdd) return;

    // Check if language already exists in summary contents
    if (
      book.summaryContents.some((content) => content.language === languageCode)
    ) {
      return;
    }

    // Create a new empty summary content for this language
    const newSummaryContent: SummaryContent = {
      id: 0, // This will be assigned by the database
      bookId: book.id,
      language: languageCode,
      contentType: 'summary',
      chapters: [],
      position: book.summaryContents.length, // Set position based on current length
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    handleInputChange("summaryContents", [
      ...book.summaryContents,
      newSummaryContent,
    ]);
    setCurrentLanguage(languageCode);
  };

  const removeLanguage = (languageCode: string) => {
    // Find remaining content after removing the specified language
    const remainingSummaryContents = book.summaryContents.filter(
      (content) => content.language !== languageCode
    );

    // Reorder positions for remaining content
    const updatedContents = remainingSummaryContents.map((content, index) => ({
      ...content,
      position: index,
    }));

    // Update the book state with the filtered and reordered contents
    handleInputChange("summaryContents", updatedContents);

    // If the current language is being removed, switch to first available language
    if (currentLanguage === languageCode) {
      if (remainingSummaryContents.length > 0) {
        const sortedContents = [...remainingSummaryContents].sort(
          (a, b) => (a.position || 0) - (b.position || 0)
        );
        setCurrentLanguage(sortedContents[0].language);
      } else {
        // If no languages left, clear the current language
        setCurrentLanguage("");
      }
    }
  };

  // Function to permanently delete a summary content from the database
  const deleteSummaryContent = async (languageCode: string) => {
    try {
      // Find the summary content to delete
      const summaryToDelete = book.summaryContents.find(
        (content) => content.language === languageCode
      );

      if (!summaryToDelete || !summaryToDelete.id) {
        console.log("Summary content not found in database yet or has no ID");
        // If it's not yet in the database, just remove it from state
        removeLanguage(languageCode);
        return;
      }

      console.log(
        `Deleting summary content ID ${summaryToDelete.id} for language ${languageCode}`
      );

      // Step 1: Delete chapters first
      const { error: chaptersError } = await supabase
        .from("chapters")
        .delete()
        .eq("summary_content_id", summaryToDelete.id);

      if (chaptersError) {
        console.error("Error deleting chapters:", chaptersError);
        toast({
          title: "Error",
          description: "Failed to delete chapters. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Step 2: Delete the summary content itself
      const { error: summaryError } = await supabase
        .from("summary_contents")
        .delete()
        .eq("id", summaryToDelete.id);

      if (summaryError) {
        console.error("Error deleting summary content:", summaryError);
        toast({
          title: "Error",
          description: "Failed to delete summary content. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Step 3: Update local state
      console.log("Summary content deleted successfully, updating state");
      removeLanguage(languageCode);

      toast({
        title: "Content Deleted",
        description: `The ${
          languages?.find((l) => l.code === languageCode)?.name || languageCode
        } content has been deleted.`,
      });
    } catch (error) {
      console.error("Error during summary content deletion:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleLanguageDelete = (languageCode: string) => {
    setLanguageToDelete(languageCode);
  };

  const confirmDeleteLanguage = async () => {
    if (languageToDelete) {
      setIsDeleteLoading(true);
      try {
        // Delete from database
        await deleteSummaryContent(languageToDelete);
      } finally {
        setIsDeleteLoading(false);
        setLanguageToDelete(null);
      }
    }
  };

  // Mutation for saving books
  const saveMutation = useMutation({
    mutationFn: async (bookData: ExtendedBookSummary) => {
      setIsSaving(true);
      try {
        console.log("Saving book with cover image:", bookData.coverImage);

        if (isNewBook) {
          // Create a new book
          const bookInsertData = {
            title: bookData.title,
            cover_image: bookData.coverImage,
            cover_bg_color: bookData.coverBgColor,
            reading_time: bookData.readingTime,
            is_active: bookData.isActive,
            is_free: bookData.isFree,
            rating: 0, // Default for new books
            view_count: 0, // Default for new books
            isbn: bookData.isbn || null,
            publisher: bookData.publisher || null,
            publication_year: bookData.publicationYear || null,
            page_count: bookData.pageCount || null,
          };

          console.log("Book insert data:", bookInsertData);

          const { data: newBook, error } = await supabase
            .from("books")
            .insert(bookInsertData)
            .select("id")
            .single();

          if (error) throw error;
          const bookId = newBook?.id;

          // Associate authors
          if (bookData.authors?.length > 0) {
            const authorsToInsert = bookData.authors
              .filter((author) => author && author.id)
              .map((author) => ({
                book_id: bookId,
                person_id: author.id,
                bio: (author as BookAuthor).bookBio || null,
              }));

            if (authorsToInsert.length > 0) {
              const { error: authorsError } = await supabase
                .from("books_authors")
                .insert(authorsToInsert);

              if (authorsError) throw authorsError;
            }
          }

          // Associate categories
          if (bookData.categories?.length > 0) {
            const categoriesToInsert = bookData.categories
              .filter((category) => category && category.id)
              .map((category) => ({
                book_id: bookId,
                category_id: category.id,
              }));

            if (categoriesToInsert.length > 0) {
              const { error: categoriesError } = await supabase
                .from("books_categories")
                .insert(categoriesToInsert);

              if (categoriesError) throw categoriesError;
            }
          }

          // Create summary contents
          if (bookData.summaryContents?.length > 0) {
            for (const content of bookData.summaryContents) {
              // Insert summary content
              const { data: summaryContent, error: summaryContentError } =
                await supabase
                  .from("summary_contents")
                  .insert({
                    book_id: bookId,
                    language: content.language,
                    content_type: content.contentType || 'summary',
                    subtitle: content.subtitle || null,
                    short_description: content.shortDescription || null,
                    audio_url: content.audioUrl || null,
                    audio_title: content.audioTitle || null,
                    translator: content.translator?.id || null,
                    audio_narrator: content.audioNarrator?.id || null,
                    audio_duration:
                      content.audioDuration === undefined ||
                      content.audioDuration === null
                        ? null
                        : Math.round(
                            parseFloat(String(content.audioDuration)) || 0
                          ),
                    ai_context: content.aiContext || null,
                    ai_context_updated_at: content.aiContext ? new Date().toISOString() : null,
                    position: content.position || 0,
                  })
                  .select("id")
                  .single();

              if (summaryContentError) {
                console.error(
                  "Error inserting summary content:",
                  summaryContentError
                );
                continue;
              }

              // Insert chapters for this summary content
              if (content.chapters && content.chapters.length > 0) {
                const chaptersToInsert = content.chapters.map(
                  (chapter, index) => ({
                    summary_content_id: summaryContent.id,
                    title: chapter.title,
                    label: chapter.label,
                    content: chapter.content,
                    position: index,
                    start_time: chapter.startTime
                      ? Math.round(chapter.startTime)
                      : null,
                  })
                );

                const { error: chaptersError } = await supabase
                  .from("chapters")
                  .insert(chaptersToInsert);

                if (chaptersError) {
                  console.error("Error inserting chapters:", chaptersError);
                }
              }
            }
          }

          return bookId;
        } else {
          // Update existing book
          const bookUpdateData = {
            title: bookData.title,
            cover_image: bookData.coverImage,
            cover_bg_color: bookData.coverBgColor,
            reading_time: bookData.readingTime,
            is_active: bookData.isActive,
            is_free: bookData.isFree,
            isbn: bookData.isbn || null,
            publisher: bookData.publisher || null,
            publication_year: bookData.publicationYear || null,
            page_count: bookData.pageCount || null,
            updated_at: new Date().toISOString(),
          };

          console.log("Book update data:", bookUpdateData);

          const { error } = await supabase
            .from("books")
            .update(bookUpdateData)
            .eq("id", bookData.id);

          if (error) throw error;

          // Update authors - first delete existing relationships
          await supabase
            .from("books_authors")
            .delete()
            .eq("book_id", bookData.id);

          // Then insert new ones
          if (bookData.authors?.length > 0) {
            const authorsToInsert = bookData.authors
              .filter((author) => author && author.id)
              .map((author) => ({
                book_id: bookData.id,
                person_id: author.id,
                bio: (author as BookAuthor).bookBio || null,
              }));

            if (authorsToInsert.length > 0) {
              const { error: authorsError } = await supabase
                .from("books_authors")
                .insert(authorsToInsert);

              if (authorsError) throw authorsError;
            }
          }

          // Update categories - first delete existing relationships
          await supabase
            .from("books_categories")
            .delete()
            .eq("book_id", bookData.id);

          // Then insert new ones
          if (bookData.categories?.length > 0) {
            const categoriesToInsert = bookData.categories
              .filter((category) => category && category.id)
              .map((category) => ({
                book_id: bookData.id,
                category_id: category.id,
              }));

            if (categoriesToInsert.length > 0) {
              const { error: categoriesError } = await supabase
                .from("books_categories")
                .insert(categoriesToInsert);

              if (categoriesError) throw categoriesError;
            }
          }

          // Save summaryContents - Process all summary contents
          if (bookData.summaryContents?.length > 0) {
            // Get existing summary contents from the database
            const { data: existingSummaryContents } = await supabase
              .from("summary_contents")
              .select("id")
              .eq("book_id", bookData.id);

            const existingIds =
              existingSummaryContents?.map((item) => item.id) || [];

            for (const content of bookData.summaryContents) {
              let summaryContentId: number;

              // Check if this is an existing content (has numeric ID) or new (has string ID like "sc-timestamp")
              const isExistingContent =
                typeof content.id === "number" &&
                existingIds.includes(content.id);

              if (isExistingContent) {
                // Update existing content (without chapters)
                summaryContentId = content.id as number;
                const { error: contentError } = await supabase
                  .from("summary_contents")
                  .update({
                    content_type: content.contentType || 'summary',
                    subtitle: content.subtitle,
                    short_description: content.shortDescription,
                    audio_title:
                      content.audioTitle === undefined
                        ? null
                        : content.audioTitle,
                    audio_narrator: content.audioNarrator?.id || null,
                    audio_duration:
                      content.audioDuration === undefined ||
                      content.audioDuration === null
                        ? null
                        : Math.round(
                            parseFloat(String(content.audioDuration)) || 0
                          ),
                    audio_url:
                      content.audioUrl === undefined ? null : content.audioUrl,
                    translator: content.translator?.id || null,
                    ai_context: content.aiContext || null,
                    ai_context_updated_at: content.aiContext ? new Date().toISOString() : null,
                    position: content.position,
                    updated_at: new Date().toISOString(),
                  })
                  .eq("id", content.id);

                console.log("Updating summary content in DB:", content.id);
                console.log(
                  "Setting audio_url to:",
                  content.audioUrl === undefined ? null : content.audioUrl
                );
                console.log(
                  "Setting audio_title to:",
                  content.audioTitle === undefined ? null : content.audioTitle
                );

                if (contentError) {
                  console.error(
                    "Error updating summary content:",
                    contentError
                  );
                  continue;
                }
              } else {
                // Insert new content
                const { data: newContent, error: contentError } = await supabase
                  .from("summary_contents")
                  .insert({
                    book_id: bookData.id,
                    language: content.language,
                    content_type: content.contentType || 'summary',
                    subtitle: content.subtitle,
                    short_description: content.shortDescription,
                    audio_title: content.audioTitle,
                    audio_narrator: content.audioNarrator?.id,
                    audio_duration:
                      content.audioDuration === undefined ||
                      content.audioDuration === null
                        ? null
                        : Math.round(
                            parseFloat(String(content.audioDuration)) || 0
                          ),
                    audio_url:
                      content.audioUrl === undefined ? null : content.audioUrl,
                    translator: content.translator?.id,
                    ai_context: content.aiContext || null,
                    ai_context_updated_at: content.aiContext ? new Date().toISOString() : null,
                    position: content.position || 0,
                  })
                  .select("id")
                  .single();

                if (contentError) {
                  console.error(
                    "Error inserting summary content:",
                    contentError
                  );
                  continue;
                }

                if (!newContent) {
                  console.error(
                    "Failed to insert summary content: No ID returned"
                  );
                  continue;
                }

                summaryContentId = newContent.id;
              }

              // Handle chapters for this summary content
              if (
                content.chapters &&
                content.chapters.length > 0 &&
                summaryContentId
              ) {
                console.log(
                  `Processing ${content.chapters.length} chapters for language ${content.language}`
                );

                // First, get existing chapters
                const { data: existingChapters } = await supabase
                  .from("chapters")
                  .select("id")
                  .eq("summary_content_id", summaryContentId);

                const existingChapterIds =
                  existingChapters?.map((ch) => ch.id) || [];

                console.log(
                  `Found ${existingChapterIds.length} existing chapters in DB for summary content ID ${summaryContentId}`
                );

                // Track which chapters have been processed to know which to delete later
                const processedChapterIds: number[] = [];

                // Process each chapter - update existing ones and insert new ones
                for (let i = 0; i < content.chapters.length; i++) {
                  const chapter = content.chapters[i];

                  // Prepare chapter data
                  const chapterData = {
                    summary_content_id: summaryContentId,
                    title: chapter.title || "",
                    label: chapter.label || "",
                    content: chapter.content || "",
                    position: i,
                    start_time: chapter.startTime
                      ? Math.round(chapter.startTime)
                      : null,
                  };

                  if (chapter.id && chapter.id > 0) {
                    // Update existing chapter
                    console.log(
                      `Updating existing chapter with ID ${chapter.id}`
                    );

                    const { data: updatedChapter, error: updateError } =
                      await supabase
                        .from("chapters")
                        .update(chapterData)
                        .eq("id", chapter.id)
                        .select("id")
                        .single();

                    if (updateError) {
                      console.error("Error updating chapter:", updateError);
                    } else if (updatedChapter) {
                      console.log(
                        `Successfully updated chapter with ID: ${updatedChapter.id}`
                      );
                      processedChapterIds.push(updatedChapter.id);
                    }
                  } else {
                    // Insert new chapter
                    console.log(`Inserting new chapter: ${chapter.title}`);

                    const { data: insertedChapter, error: insertError } =
                      await supabase
                        .from("chapters")
                        .insert(chapterData)
                        .select("id")
                        .single();

                    if (insertError) {
                      console.error("Error inserting chapter:", insertError);
                    } else if (insertedChapter) {
                      console.log(
                        `Successfully inserted chapter with ID: ${insertedChapter.id}`
                      );
                      processedChapterIds.push(insertedChapter.id);
                    }
                  }
                }

                // Delete chapters that are no longer present
                const chaptersToDelete = existingChapterIds.filter(
                  (id) => !processedChapterIds.includes(id)
                );

                if (chaptersToDelete.length > 0) {
                  console.log(
                    `Deleting ${chaptersToDelete.length} chapters that are no longer needed`
                  );

                  const { error: deleteError } = await supabase
                    .from("chapters")
                    .delete()
                    .in("id", chaptersToDelete);

                  if (deleteError) {
                    console.error(
                      "Error deleting unused chapters:",
                      deleteError
                    );
                  } else {
                    console.log(
                      `Successfully deleted ${chaptersToDelete.length} unused chapters`
                    );
                  }
                }
              }
            }
          }

          return bookData.id;
        }
      } finally {
        setIsSaving(false);
      }
    },
    onSuccess: (bookId) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["books"] });
      queryClient.invalidateQueries({ queryKey: ["book", bookId] });

      toast({
        title: isNewBook ? "Book created" : "Book updated",
        description: `"${book.title}" has been ${
          isNewBook ? "created" : "updated"
        } successfully.`,
      });

      // Navigate to the appropriate page
      if (isNewBook) {
        navigate("/admin/books");
      } else if (id) {
        navigate(`/admin/books/${id}`);
      } else {
        navigate("/admin/books");
      }
    },
    onError: (error) => {
      console.error("Error saving book:", error);
      toast({
        title: "Error",
        description: `Failed to ${
          isNewBook ? "create" : "update"
        } book. Please try again.`,
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      if (!id) throw new Error("Book ID is required for deletion");

      try {
        console.log("Deleting book with ID:", id);

        // Step 1: Delete book_views (analytics data)
        console.log(`Deleting view analytics for book ID: ${id}`);
        try {
          const { error: viewsError } = await supabase
            .from("book_views")
            .delete()
            .eq("book_id", id);

          if (viewsError && !viewsError.message.includes("does not exist")) {
            console.error("Error deleting book views:", viewsError);
            // Continue deletion process even if views deletion fails
          }
        } catch (viewError) {
          // If book_views table doesn't exist, just continue
          console.log("Skipping book_views deletion:", viewError);
        }

        // Step 2: Delete book_reads (reading analytics)
        console.log(`Deleting read analytics for book ID: ${id}`);
        try {
          const { error: readsError } = await supabase
            .from("book_reads")
            .delete()
            .eq("book_id", id);

          if (readsError && !readsError.message.includes("does not exist")) {
            console.error("Error deleting book reads:", readsError);
            // Continue deletion process even if reads deletion fails
          }
        } catch (readsError) {
          // If book_reads table doesn't exist, just continue
          console.log("Skipping book_reads deletion:", readsError);
        }

        // Step 3: Delete book_audio_plays (audio analytics)
        console.log(`Deleting audio play analytics for book ID: ${id}`);
        try {
          const { error: audioPlaysError } = await supabase
            .from("book_audio_plays")
            .delete()
            .eq("book_id", id);

          if (
            audioPlaysError &&
            !audioPlaysError.message.includes("does not exist")
          ) {
            console.error("Error deleting book audio plays:", audioPlaysError);
            // Continue deletion process even if audio plays deletion fails
          }
        } catch (audioPlaysError) {
          // If book_audio_plays table doesn't exist, just continue
          console.log("Skipping book_audio_plays deletion:", audioPlaysError);
        }

        // Step 4: Find all summary_contents for this book
        const { data: summaryContents, error: fetchError } = await supabase
          .from("summary_contents")
          .select("id")
          .eq("book_id", id);

        if (fetchError) {
          console.error("Error fetching summary contents:", fetchError);
          throw fetchError;
        }

        // Step 5: Delete chapters for each summary content
        if (summaryContents && summaryContents.length > 0) {
          const summaryContentIds = summaryContents.map(
            (content) => content.id
          );
          console.log(
            `Deleting chapters for ${summaryContentIds.length} summary contents`
          );

          const { error: chaptersError } = await supabase
            .from("chapters")
            .delete()
            .in("summary_content_id", summaryContentIds);

          if (chaptersError) {
            console.error("Error deleting chapters:", chaptersError);
            throw chaptersError;
          }
        }

        // Step 6: Delete summary_contents for this book
        console.log(`Deleting summary contents for book ID: ${id}`);
        const { error: summaryContentsError } = await supabase
          .from("summary_contents")
          .delete()
          .eq("book_id", id);

        if (summaryContentsError) {
          console.error(
            "Error deleting summary contents:",
            summaryContentsError
          );
          throw summaryContentsError;
        }

        // Step 7: Delete author relationships
        console.log(`Deleting author relationships for book ID: ${id}`);
        const { error: authorsError } = await supabase
          .from("books_authors")
          .delete()
          .eq("book_id", id);

        if (authorsError) {
          console.error("Error deleting book authors:", authorsError);
          throw authorsError;
        }

        // Step 8: Delete category relationships
        console.log(`Deleting category relationships for book ID: ${id}`);
        const { error: categoriesError } = await supabase
          .from("books_categories")
          .delete()
          .eq("book_id", id);

        if (categoriesError) {
          console.error("Error deleting book categories:", categoriesError);
          throw categoriesError;
        }

        // Step 9: Finally delete the book itself
        console.log(`Deleting the book ID: ${id}`);
        const { error } = await supabase.from("books").delete().eq("id", id);

        if (error) {
          console.error("Error deleting book:", error);
          throw error;
        }

        return id;
      } catch (error) {
        console.error("Error during deletion process:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });

      toast({
        title: "Book deleted",
        description: `"${book.title}" has been deleted.`,
        variant: "destructive",
      });

      navigate("/admin/books");
    },
    onError: (error) => {
      console.error("Error deleting book:", error);
      toast({
        title: "Error",
        description: "Failed to delete book. Please try again.",
        variant: "destructive",
      });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setBook((prev) => {
        const oldIndex = prev.summaryContents.findIndex(
          (content) => content.language === active.id
        );
        const newIndex = prev.summaryContents.findIndex(
          (content) => content.language === over.id
        );

        // Get the reordered array
        const newSummaryContents = arrayMove(
          prev.summaryContents,
          oldIndex,
          newIndex
        );

        // Update positions based on new order
        const updatedContents = newSummaryContents.map((content, index) => ({
          ...content,
          position: index,
        }));

        return {
          ...prev,
          summaryContents: updatedContents,
        };
      });
    }
  };

  // Check if the current language has audio
  const currentLanguageHasAudio =
    book.summaryContents?.find(
      (content) => content.language === currentLanguage
    )?.audioUrl !== undefined;

  // Handle back navigation
  const handleBackNavigation = () => {
    navigate(-1);
  };

  // Explicitly determine if we should show the delete button
  const shouldShowDeleteButton = () => {
    // For debugging - remove in production
    console.log("ID parameter:", id);
    console.log("isNewBook:", isNewBook);
    console.log("Book Object ID:", book.id);
    console.log("Book Object type:", typeof book.id);

    // Check if the current path contains "/new"
    if (window.location.pathname.includes("/new")) {
      return false;
    }

    // Never show delete button for new books
    if (id === "new" || isNewBook) {
      return false;
    }

    // Only show for existing books with valid IDs
    return !!book.id && typeof book.id === "number" && book.id > 0;
  };

  // Add utility functions to work with summaryContents
  const updateSummaryContent = (
    language: string,
    updates: Partial<SummaryContent>
  ) => {
    console.log("Updating summary content for language:", language);
    console.log("Updates:", updates);

    const otherContents =
      book.summaryContents?.filter((c) => c.language !== language) || [];
    const existingContent = book.summaryContents?.find(
      (c) => c.language === language
    );

    const baseContent: SummaryContent = existingContent || {
      id: existingContent ? existingContent.id : 0,
      bookId:
        typeof book.id === "string" ? parseInt(book.id) || 0 : book.id || 0,
      language,
      chapters: [],
      position: book.summaryContents.length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Handle chapters separately to avoid overwriting with potentially stale data
    let updatedChapters = baseContent.chapters;
    if (updates.chapters) {
      console.log("Received updated chapters:", updates.chapters);
      updatedChapters = updates.chapters;

      // No need to regenerate IDs for existing chapters
      // Just ensure each chapter has correct position
      updatedChapters = updatedChapters.map((chapter, index) => ({
        ...chapter,
        position: index,
      }));

      console.log("Final processed chapters:", updatedChapters);

      // Remove chapters property so we don't overwrite it in the spread below
      delete updates.chapters;
    }

    // Create the updated content with proper type safety
    const updatedContent: SummaryContent = {
      ...baseContent,
      ...updates,
      chapters: updatedChapters, // Add chapters back
    };

    console.log("Final updated content:", updatedContent);

    // Create a new array of summary contents with the updated content in the correct position
    const newSummaryContents = [...book.summaryContents];
    const existingIndex = newSummaryContents.findIndex(
      (c) => c.language === language
    );

    if (existingIndex !== -1) {
      // Replace existing content
      newSummaryContents[existingIndex] = updatedContent;
    } else {
      // Add new content
      newSummaryContents.push(updatedContent);
    }

    // Update the book state with the new array of summary contents
    handleInputChange("summaryContents", newSummaryContents);
  };

  // Get short description for the current language
  const currentShortDescription =
    book.summaryContents?.find(
      (content) => content.language === currentLanguage
    )?.shortDescription || "";

  const saveBook = () => {
    console.log("Saving book with final state:", {
      id: book.id,
      title: book.title,
      coverImage: book.coverImage,
      coverBgColor: book.coverBgColor,
      isActive: book.isActive, // Log the isActive status
    });
    saveMutation.mutate(book);
  };

  const deleteBook = () => {
    console.log("Opening delete confirmation dialog for book:", book.id);
    // Store the ID separately for deletion
    setBookIdToDelete(book.id);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmedDelete = () => {
    console.log("Delete confirmed for book ID:", bookIdToDelete);
    if (bookIdToDelete) {
      // Use the stored book ID for deletion
      deleteMutation.mutate(bookIdToDelete);
    } else {
      console.error("No book ID available for deletion");
      toast({
        title: "Error",
        description: "Could not delete book: Book ID not found.",
        variant: "destructive",
      });
    }
    setShowDeleteConfirmation(false);
  };

  // Before the return statement, add this helper function
  const toggleSectionExpansion =
    (
      sectionType:
        | "isDescriptionExpanded"
        | "isAudioExpanded"
        | "isChaptersExpanded"
    ) =>
    (e?: React.MouseEvent) => {
      // If event provided, stop propagation (for button clicks)
      if (e) {
        e.stopPropagation();
      }

      // Get current expanded state
      const currentContent = book.summaryContents?.find(
        (content) => content.language === currentLanguage
      );

      if (!currentContent) return;

      // Get current value with default true
      const isCurrentlyExpanded = currentContent[sectionType] ?? true;

      // Create updated content with toggled expansion state
      const updatedContent = {
        ...currentContent,
        [sectionType]: !isCurrentlyExpanded,
      };

      // Create a new array of summary contents with the updated content in the correct position
      const newSummaryContents = [...book.summaryContents];
      const existingIndex = newSummaryContents.findIndex(
        (c) => c.language === currentLanguage
      );

      if (existingIndex !== -1) {
        // Replace existing content
        newSummaryContents[existingIndex] = updatedContent;
        // Update the book state with the new array of summary contents
        handleInputChange("summaryContents", newSummaryContents);
      }
    };

  // Generate a safe filename to avoid collisions while preserving the original name
  const generateSafeFilename = (filename: string): string => {
    // Extract the file extension
    const lastDotIndex = filename.lastIndexOf(".");
    const ext = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : "";
    const nameWithoutExt =
      lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;

    // Clean up the filename:
    // 1. Decode URI components to handle special characters
    // 2. Replace spaces with hyphens
    // 3. Remove any special characters except hyphens and alphanumeric
    // 4. Convert to lowercase for consistency
    const cleanName = decodeURIComponent(nameWithoutExt)
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-]/g, "")
      .toLowerCase();

    // Add a short timestamp (last 4 digits) to ensure uniqueness
    const timestamp = new Date().getTime().toString().slice(-4);

    return `${cleanName}-${timestamp}${ext}`;
  };

  if (
    (isLoadingBook && !isNewBook) ||
    isLoadingCategories ||
    isLoadingLanguages ||
    !categories ||
    !languages
  ) {
    return (
      <AdminLayout title="Edit Book">
        <div className="flex items-center justify-center h-full">
          {error ? (
            <div className="text-center space-y-4">
              <div className="text-destructive text-xl">{error}</div>
              <Button onClick={() => navigate("/admin/books")}>
                Return to Books List
              </Button>
            </div>
          ) : (
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          )}
        </div>
      </AdminLayout>
    );
  }

  const selectedCategoryIds =
    book.categories
      ?.filter((category) => category && category.id)
      ?.map((c) => c.id) || [];

  return (
    <AdminLayout title={isNewBook ? "New Book" : "Edit Book"}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleBackNavigation}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isNewBook ? "New Book" : book.title}
          </h1>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handleBackNavigation}>
            Cancel
          </Button>
          <Button
            onClick={saveBook}
            disabled={isSaving || saveMutation.isPending}
          >
            {isSaving || saveMutation.isPending ? (
              <div className="animate-spin h-4 w-4 mr-2 border-2 border-background rounded-full border-t-transparent"></div>
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </Button>
        </div>
      </div>

      <Tabs defaultValue="basic">
        <TabsList className="mb-6">
          <TabsTrigger value="basic">Basic Information</TabsTrigger>
          <TabsTrigger value="content">Editions</TabsTrigger>
          <TabsTrigger value="ai-context">AI Context</TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Book Details</CardTitle>
                  <CardDescription>
                    Enter the basic information about the book
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={book.title}
                      onChange={(e) =>
                        handleInputChange("title", e.target.value)
                      }
                      placeholder="Enter book title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="authors">Authors</Label>
                    <div className="relative">
                      <Button
                        variant="outline"
                        onClick={() => {
                          document
                            .getElementById("authors-dropdown")
                            ?.classList.toggle("hidden");
                        }}
                        className="w-full justify-between"
                        id="authors"
                      >
                        <span className="truncate">
                          {book.authors?.length === 0
                            ? "Select authors"
                            : `${book.authors?.length} ${
                                book.authors?.length === 1
                                  ? "author"
                                  : "authors"
                              } selected`}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                      <div
                        className="absolute z-10 w-full mt-1 rounded-md border bg-popover shadow-md hidden"
                        id="authors-dropdown"
                      >
                        <div className="max-h-60 overflow-auto p-1">
                          {authorsList.map((author) => (
                            <div
                              key={author?.id || "unknown"}
                              className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                              onClick={() => {
                                const isSelected = book.authors?.some(
                                  (a) => a?.id === author?.id
                                );
                                const updatedAuthors = isSelected
                                  ? book.authors?.filter(
                                      (a) => a?.id !== author?.id
                                    )
                                  : [...(book.authors || []), author];
                                handleInputChange("authors", updatedAuthors);
                              }}
                            >
                              <span className="mr-2">
                                {book.authors?.some(
                                  (a) => a?.id === author?.id
                                ) ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <div className="h-4 w-4 rounded-sm border" />
                                )}
                              </span>
                              {author?.name || "Unknown author"}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {book.authors?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {book.authors
                          ?.filter((author) => author && author.id)
                          ?.map((author) => (
                            <Badge
                              key={author?.id || "unknown"}
                              variant="secondary"
                              className="gap-1"
                            >
                              {author?.name || "Unknown author"}
                              <button
                                className="ml-1 rounded-full outline-none focus:ring-2"
                                onClick={() => {
                                  handleInputChange(
                                    "authors",
                                    book.authors?.filter(
                                      (a) => a?.id !== author?.id
                                    ) || []
                                  );
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                      </div>
                    )}
                    {book.authors?.length > 0 && (
                      <div className="space-y-3 mt-3">
                        <Label>Per-Book Author Bios</Label>
                        {book.authors
                          ?.filter((author) => author && author.id)
                          ?.map((author) => (
                            <div key={`bio-${author.id}`} className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                {author.name}
                              </Label>
                              <Textarea
                                placeholder={`Bio for ${author.name} in this book...`}
                                value={(author as BookAuthor).bookBio || ""}
                                onChange={(e) => {
                                  const updatedAuthors = book.authors?.map((a) =>
                                    a.id === author.id
                                      ? { ...a, bookBio: e.target.value }
                                      : a
                                  ) || [];
                                  handleInputChange("authors", updatedAuthors);
                                }}
                                rows={2}
                              />
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="categories">Categories</Label>
                    <div className="relative">
                      <Button
                        variant="outline"
                        onClick={() => {
                          document
                            .getElementById("categories-dropdown")
                            ?.classList.toggle("hidden");
                        }}
                        className="w-full justify-between"
                        id="categories"
                      >
                        <span className="truncate">
                          {selectedCategoryIds.length === 0
                            ? "Select categories"
                            : `${selectedCategoryIds.length} ${
                                selectedCategoryIds.length === 1
                                  ? "category"
                                  : "categories"
                              } selected`}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                      <div
                        className="absolute z-10 w-full mt-1 rounded-md border bg-popover shadow-md hidden"
                        id="categories-dropdown"
                      >
                        <div className="max-h-60 overflow-auto p-1">
                          {categories.map((category) => (
                            <div
                              key={category.id}
                              className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                              onClick={() => {
                                const isSelected = selectedCategoryIds.includes(
                                  category.id
                                );
                                const updatedIds = isSelected
                                  ? selectedCategoryIds.filter(
                                      (id) => id !== category.id
                                    )
                                  : [...selectedCategoryIds, category.id];
                                handleCategoryChange(updatedIds);
                              }}
                            >
                              <span className="mr-2">
                                {selectedCategoryIds.includes(category.id) ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <div className="h-4 w-4 rounded-sm border" />
                                )}
                              </span>
                              {category.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {selectedCategoryIds.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedCategoryIds.map((id) => {
                          const category = categories.find((c) => c.id === id);
                          return (
                            <Badge
                              key={id}
                              variant="secondary"
                              className="gap-1"
                            >
                              {category?.name}
                              <button
                                className="ml-1 rounded-full outline-none focus:ring-2"
                                onClick={() => {
                                  handleCategoryChange(
                                    selectedCategoryIds.filter(
                                      (catId) => catId !== id
                                    )
                                  );
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="readingTime">Reading Time (minutes)</Label>
                    <Input
                      id="readingTime"
                      type="number"
                      min="0"
                      value={book.readingTime}
                      onChange={(e) =>
                        handleInputChange(
                          "readingTime",
                          parseInt(e.target.value) || 0
                        )
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="isbn">ISBN</Label>
                      <Input
                        id="isbn"
                        value={book.isbn || ""}
                        onChange={(e) =>
                          handleInputChange("isbn", e.target.value || "")
                        }
                        placeholder="e.g. 978-3-16-148410-0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="publisher">Publisher</Label>
                      <Input
                        id="publisher"
                        value={book.publisher || ""}
                        onChange={(e) =>
                          handleInputChange("publisher", e.target.value || "")
                        }
                        placeholder="Publisher name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="publicationYear">Publication Year</Label>
                      <Input
                        id="publicationYear"
                        type="number"
                        min="1800"
                        max="2100"
                        value={book.publicationYear || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "publicationYear",
                            e.target.value ? parseInt(e.target.value) : 0
                          )
                        }
                        placeholder="e.g. 2024"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pageCount">Page Count</Label>
                      <Input
                        id="pageCount"
                        type="number"
                        min="0"
                        value={book.pageCount || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "pageCount",
                            e.target.value ? parseInt(e.target.value) : 0
                          )
                        }
                        placeholder="Number of pages"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={Boolean(book.isActive)}
                        onCheckedChange={(checked) =>
                          handleInputChange("isActive", checked)
                        }
                        className="data-[state=checked]:bg-green-500"
                      />
                      <span className="text-sm text-muted-foreground">
                        {book.isActive ? "Published" : "Unpublished"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Access Type</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={Boolean(book.isFree)}
                        onCheckedChange={(checked) =>
                          handleInputChange("isFree", checked)
                        }
                        className="data-[state=checked]:bg-blue-500"
                      />
                      <span className="text-sm text-muted-foreground">
                        {book.isFree ? "Free" : "Premium"}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Cover Image</CardTitle>
                  <CardDescription>
                    Upload or provide a URL for the book cover
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    {/* Cover Preview - Full Width Row */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">Cover Preview</p>
                        <p className="text-xs text-muted-foreground">
                          16:9 aspect ratio preview
                        </p>
                      </div>
                      <div
                        className="w-full relative rounded-lg overflow-hidden border aspect-video"
                        style={{
                          backgroundColor: "#1F2532",
                        }}
                      >
                        {/* Dynamic background color overlay with 80% transparency */}
                        <div
                          className="absolute inset-0"
                          style={{
                            backgroundColor: book.coverBgColor || "#E9ECEF",
                            opacity: 0.8,
                          }}
                        />
                        {book.coverImage ? (
                          <div className="absolute inset-0 flex items-center justify-center p-4 z-10">
                            <img
                              src={book.coverImage}
                              alt={book.title || "Book cover"}
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground z-10">
                            No cover image selected
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Controls - One line each for Cover Image and Background Color */}
                    <div className="space-y-4">
                      {/* Cover Image Selection */}
                      <div>
                        <Label htmlFor="coverImage" className="mb-2 block">
                          Cover Image
                        </Label>
                        <MediaPicker
                          onSelect={(url) => {
                            console.log("MediaPicker selected URL:", url);
                            console.log(
                              "Current book state before update:",
                              book
                            );
                            handleInputChange("coverImage", url);
                            setTimeout(() => {
                              console.log(
                                "Book state after coverImage update:",
                                book
                              );
                            }, 0);
                          }}
                          currentValue={book.coverImage || ""}
                          allowedTypes={[
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                          ]}
                          title="Select Book Cover"
                          description="Choose a cover image for this book from your media library or upload a new one"
                          buttonText="Select Cover Image"
                          showPreview={false}
                        />
                      </div>

                      {/* Background Color Selection */}
                      <div>
                        <Label htmlFor="coverBgColor" className="mb-2 block">
                          Background Color
                        </Label>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="h-9 w-9 rounded border flex items-center justify-center overflow-hidden">
                            <input
                              type="color"
                              id="coverBgColor"
                              value={book.coverBgColor || "#E9ECEF"}
                              onChange={(e) =>
                                handleInputChange(
                                  "coverBgColor",
                                  e.target.value
                                )
                              }
                              className="w-12 h-12 border-0 p-0 m-0 cursor-pointer"
                              style={{ transform: "scale(1.5)" }}
                            />
                          </div>
                          <Input
                            value={book.coverBgColor || "#E9ECEF"}
                            onChange={(e) =>
                              handleInputChange("coverBgColor", e.target.value)
                            }
                            placeholder="#E9ECEF"
                            className="font-mono w-32"
                            maxLength={7}
                          />

                          {/* Quick colors */}
                          <div className="flex flex-wrap gap-1 ml-2">
                            {[
                              "#F8F9FA",
                              "#E9ECEF",
                              "#F8F0E3",
                              "#FFF3BF",
                              "#FFE8CC",
                              "#D8F3DC",
                              "#D7E3FC",
                              "#EFD9CE",
                              "#212529",
                            ].map((color) => (
                              <button
                                key={color}
                                className="w-6 h-6 rounded-full border border-border hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-primary"
                                style={{ backgroundColor: color }}
                                onClick={() =>
                                  handleInputChange("coverBgColor", color)
                                }
                                title={color}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          This color will be used as a background when
                          displaying the book cover
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Preview</CardTitle>
                  <CardDescription>
                    See how the summary will appear
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4 space-y-4">
                    {book.coverImage ? (
                      <div
                        className="w-full h-48 rounded-md mb-4 flex items-center justify-center p-3 relative"
                        style={{ backgroundColor: "#1F2532" }}
                      >
                        {/* Dynamic background color overlay with 80% transparency */}
                        <div
                          className="absolute inset-0 rounded-md"
                          style={{
                            backgroundColor: book.coverBgColor || "#E9ECEF",
                            opacity: 0.8,
                          }}
                        />
                        <img
                          src={book.coverImage}
                          alt={book.title}
                          className="max-h-40 max-w-full object-contain rounded relative z-10"
                        />
                      </div>
                    ) : (
                      <div
                        className="w-full h-48 rounded-md flex items-center justify-center mb-4 relative"
                        style={{ backgroundColor: "#1F2532" }}
                      >
                        {/* Dynamic background color overlay with 80% transparency */}
                        <div
                          className="absolute inset-0 rounded-md"
                          style={{
                            backgroundColor: book.coverBgColor || "#E9ECEF",
                            opacity: 0.8,
                          }}
                        />
                        <p className="text-muted-foreground relative z-10">
                          No cover image
                        </p>
                      </div>
                    )}

                    <h3 className="font-semibold text-lg">
                      {book.title || "Book Title"}
                    </h3>
                    {book.summaryContents?.find(
                      (content) => content.language === currentLanguage
                    )?.subtitle && (
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        {
                          book.summaryContents.find(
                            (content) => content.language === currentLanguage
                          )?.subtitle
                        }
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      by{" "}
                      {book.authors?.length > 0
                        ? book.authors
                            ?.filter((author) => author && author.name)
                            ?.map((author) => author.name)
                            .join(", ")
                        : "No authors added"}
                    </p>
                    {book.summaryContents?.find(
                      (content) => content.language === currentLanguage
                    )?.translator && (
                      <p className="text-xs text-muted-foreground">
                        Translated by{" "}
                        {
                          book.summaryContents.find(
                            (content) => content.language === currentLanguage
                          )?.translator?.name
                        }
                      </p>
                    )}

                    <div className="text-sm">
                      {book.readingTime > 0
                        ? `${book.readingTime} min read`
                        : "Reading time not set"}
                    </div>

                    {book.categories?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {book.categories
                          ?.filter((category) => category && category.id)
                          ?.map((category) => (
                            <span
                              key={category.id}
                              className="text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded-full"
                            >
                              {category.name}
                            </span>
                          ))}
                      </div>
                    )}

                    {book.summaryContents.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          Available in {book.summaryContents.length}{" "}
                          {book.summaryContents.length === 1
                            ? "language"
                            : "languages"}
                        </span>
                      </div>
                    )}

                    {book.summaryContents?.some(
                      (content) =>
                        content.audioUrl && content.audioUrl.trim() !== ""
                    ) && (
                      <div className="flex items-center gap-2 mt-2">
                        <Headphones className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          Audio version available
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="content">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h2 className="text-2xl font-bold">Book Editions</h2>
                <p className="text-sm text-muted-foreground">
                  Add and manage editions (summaries or full books) in different languages
                </p>
              </div>
              <div className="flex items-center gap-4">
                <Select
                  value=""
                  onValueChange={(value) => {
                    const languageToAdd = languages?.find(
                      (lang) => lang.code === value
                    );
                    if (!languageToAdd) return;

                    // Check if language already exists
                    if (
                      book.summaryContents.some(
                        (content) => content.language === value
                      )
                    ) {
                      toast({
                        title: "Language already added",
                        description:
                          "This language has already been added to the book summary.",
                        variant: "destructive",
                      });
                      return;
                    }

                    // Add the language as a new edition
                    const newSummaryContent: SummaryContent = {
                      id: 0,
                      bookId:
                        typeof book.id === "string"
                          ? parseInt(book.id) || 0
                          : book.id || 0,
                      language: value,
                      contentType: 'summary',
                      chapters: [],
                      position: book.summaryContents.length,
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    };

                    handleInputChange("summaryContents", [
                      ...book.summaryContents,
                      newSummaryContent,
                    ]);
                    setCurrentLanguage(value);

                    toast({
                      title: "Language added",
                      description: `${languageToAdd.name} has been added successfully.`,
                    });
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue
                      placeholder={
                        book.summaryContents.length === 0
                          ? "Choose a language"
                          : "Add another language"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {languages
                      ?.filter(
                        (lang) =>
                          !book.summaryContents.some(
                            (content) => content.language === lang.code
                          )
                      )
                      .map((language) => (
                        <SelectItem key={language.code} value={language.code}>
                          <div className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            {language.name}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {book.summaryContents.length === 0 ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <Globe className="h-12 w-12 text-muted-foreground mx-auto" />
                    <div>
                      <h3 className="text-lg font-semibold mb-1">
                        No Editions Added
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Choose a language from the dropdown above to start
                        adding an edition
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={book.summaryContents
                        .slice()
                        .sort((a, b) => (a.position || 0) - (b.position || 0))
                        .map((content) => content.language)}
                      strategy={verticalListSortingStrategy}
                    >
                      {book.summaryContents
                        .slice()
                        .sort((a, b) => (a.position || 0) - (b.position || 0))
                        .map((content) => {
                          // Find the language details from the language code in content
                          const language = languages?.find(
                            (lang) => lang.code === content.language
                          );

                          if (!language) return null;

                          return (
                            <SortableLanguageItem
                              key={content.language}
                              language={language}
                              hasAudio={!!content.audioUrl}
                              contentType={content.contentType}
                              onDelete={handleLanguageDelete}
                              isSelected={content.language === currentLanguage}
                              onSelect={setCurrentLanguage}
                            />
                          );
                        })}
                    </SortableContext>
                  </DndContext>
                </div>

                <AlertDialog
                  open={!!languageToDelete}
                  onOpenChange={(open) => {
                    if (!open) setLanguageToDelete(null);
                  }}
                >
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Delete Summary Content
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete the{" "}
                        {
                          languages?.find((l) => l.code === languageToDelete)
                            ?.name
                        }{" "}
                        language content from the database? This operation will:
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Delete all chapters in this language</li>
                          <li>Remove all translations and audio content</li>
                          <li>This action cannot be undone</li>
                        </ul>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={isDeleteLoading}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={confirmDeleteLanguage}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={isDeleteLoading}
                      >
                        {isDeleteLoading ? (
                          <>
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent"></div>
                            Deleting...
                          </>
                        ) : (
                          "Delete"
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {!currentLanguage ? (
                  <Card>
                    <CardContent className="p-6">
                      <div className="text-center space-y-4">
                        <Globe className="h-12 w-12 text-muted-foreground mx-auto" />
                        <div>
                          <h3 className="text-lg font-semibold mb-1">
                            Select a Language
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Click on one of the language buttons above to edit
                            content
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <Card>
                      <CardHeader
                        className="flex flex-row items-center justify-between cursor-pointer"
                        onClick={toggleSectionExpansion(
                          "isDescriptionExpanded"
                        )}
                      >
                        <div>
                          <CardTitle>Description</CardTitle>
                          <CardDescription>
                            Create a concise description of the book in{" "}
                            {
                              languages.find((l) => l.code === currentLanguage)
                                ?.name
                            }{" "}
                            language
                          </CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          {book.summaryContents?.find(
                            (content) => content.language === currentLanguage
                          )?.isDescriptionExpanded !== false ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </CardHeader>

                      {book.summaryContents?.find(
                        (content) => content.language === currentLanguage
                      )?.isDescriptionExpanded !== false && (
                        <CardContent>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>Content Type</Label>
                              <Select
                                value={
                                  book.summaryContents?.find(
                                    (content) =>
                                      content.language === currentLanguage
                                  )?.contentType || "summary"
                                }
                                onValueChange={(value) => {
                                  updateSummaryContent(currentLanguage, {
                                    contentType: value as 'summary' | 'full_book',
                                  });
                                }}
                              >
                                <SelectTrigger className="w-[200px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="summary">Summary</SelectItem>
                                  <SelectItem value="full_book">Full Book</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                {book.summaryContents?.find(
                                  (content) =>
                                    content.language === currentLanguage
                                )?.contentType === "full_book"
                                  ? "Full book content — complete text and/or audio"
                                  : "Summary — condensed version of the book"}
                              </p>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="subtitle">Subtitle</Label>
                              <Input
                                id="subtitle"
                                value={
                                  book.summaryContents?.find(
                                    (content) =>
                                      content.language === currentLanguage
                                  )?.subtitle || ""
                                }
                                onChange={(e) => {
                                  // Update subtitle in summaryContents
                                  updateSummaryContent(currentLanguage, {
                                    subtitle: e.target.value,
                                  });
                                }}
                                placeholder={`Enter subtitle in ${
                                  languages.find(
                                    (l) => l.code === currentLanguage
                                  )?.name
                                }`}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="translator">Translator</Label>
                              <Select
                                value={
                                  book.summaryContents
                                    ?.find(
                                      (content) =>
                                        content.language === currentLanguage
                                    )
                                    ?.translator?.id?.toString() || "none"
                                }
                                onValueChange={(translatorId) => {
                                  if (translatorId === "none") {
                                    // Create a new version of summaryContent with translator set to undefined
                                    const currentContent =
                                      book.summaryContents?.find(
                                        (content) =>
                                          content.language === currentLanguage
                                      );

                                    if (currentContent) {
                                      const updatedContent = {
                                        ...currentContent,
                                      };
                                      updatedContent.translator = undefined;

                                      const otherContents =
                                        book.summaryContents?.filter(
                                          (content) =>
                                            content.language !== currentLanguage
                                        ) || [];

                                      const newSummaryContents = [
                                        ...book.summaryContents,
                                      ];
                                      const existingIndex =
                                        newSummaryContents.findIndex(
                                          (c) => c.language === currentLanguage
                                        );

                                      if (existingIndex !== -1) {
                                        newSummaryContents[existingIndex] =
                                          updatedContent;
                                        handleInputChange(
                                          "summaryContents",
                                          newSummaryContents
                                        );
                                      } else {
                                        handleInputChange("summaryContents", [
                                          ...otherContents,
                                          updatedContent,
                                        ]);
                                      }
                                    }
                                  } else {
                                    // Find the selected translator person
                                    const selectedTranslator =
                                      peopleData?.data.find(
                                        (person) =>
                                          person.id.toString() === translatorId
                                      );

                                    if (selectedTranslator) {
                                      // Create a new version of summaryContent with the translator
                                      const currentContent =
                                        book.summaryContents?.find(
                                          (content) =>
                                            content.language === currentLanguage
                                        );

                                      if (currentContent) {
                                        const updatedContent = {
                                          ...currentContent,
                                        };
                                        updatedContent.translator =
                                          selectedTranslator;

                                        const otherContents =
                                          book.summaryContents?.filter(
                                            (content) =>
                                              content.language !==
                                              currentLanguage
                                          ) || [];

                                        const newSummaryContents = [
                                          ...book.summaryContents,
                                        ];
                                        const existingIndex =
                                          newSummaryContents.findIndex(
                                            (c) =>
                                              c.language === currentLanguage
                                          );

                                        if (existingIndex !== -1) {
                                          newSummaryContents[existingIndex] =
                                            updatedContent;
                                          handleInputChange(
                                            "summaryContents",
                                            newSummaryContents
                                          );
                                        } else {
                                          handleInputChange("summaryContents", [
                                            ...otherContents,
                                            updatedContent,
                                          ]);
                                        }
                                      } else {
                                        // Create a new content entry if none exists for this language
                                        const newContent: SummaryContent = {
                                          id: 0,
                                          bookId:
                                            typeof book.id === "string"
                                              ? parseInt(book.id) || 0
                                              : book.id || 0,
                                          language: currentLanguage,
                                          chapters: [],
                                          translator: selectedTranslator,
                                          position: book.summaryContents.length,
                                          createdAt: new Date().toISOString(),
                                          updatedAt: new Date().toISOString(),
                                        };

                                        handleInputChange("summaryContents", [
                                          ...(book.summaryContents || []),
                                          newContent,
                                        ]);
                                      }
                                    }
                                  }
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue
                                    placeholder={`Select translator for ${
                                      languages.find(
                                        (l) => l.code === currentLanguage
                                      )?.name
                                    } (optional)`}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {translators?.map((person) => (
                                    <SelectItem
                                      key={person.id}
                                      value={person.id.toString()}
                                    >
                                      {person.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <ShortDescriptionEditor
                              content={currentShortDescription}
                              language={currentLanguage}
                              onChange={(shortDescription) => {
                                updateSummaryContent(currentLanguage, {
                                  shortDescription,
                                });
                              }}
                            />
                          </div>
                        </CardContent>
                      )}
                    </Card>

                    <Card>
                      <CardHeader
                        className="flex flex-row items-center justify-between cursor-pointer"
                        onClick={toggleSectionExpansion("isAudioExpanded")}
                      >
                        <div>
                          <CardTitle className="text-base">Audio</CardTitle>
                          <CardDescription className="text-xs">
                            Audio narration for{" "}
                            {languages.find((l) => l.code === currentLanguage)
                              ?.name || "selected"}
                          </CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          {book.summaryContents?.find(
                            (content) => content.language === currentLanguage
                          )?.isAudioExpanded !== false ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </CardHeader>

                      {book.summaryContents?.find(
                        (content) => content.language === currentLanguage
                      )?.isAudioExpanded !== false && (
                        <CardContent className="pt-0">
                          <AudioUploader
                            currentSummaryContent={book.summaryContents?.find(
                              (content) => content.language === currentLanguage
                            )}
                            languages={languages}
                            selectedLanguage={currentLanguage}
                            onAudioChange={(audioProperties) => {
                              // Always update the summary content with the provided properties
                              // If audioProperties contains undefined values, they will be properly
                              // converted to null in the database
                              updateSummaryContent(
                                currentLanguage,
                                audioProperties
                              );
                            }}
                          />
                        </CardContent>
                      )}
                    </Card>

                    <Card>
                      <CardHeader
                        className="flex flex-row items-center justify-between cursor-pointer"
                        onClick={toggleSectionExpansion("isChaptersExpanded")}
                      >
                        <div>
                          <CardTitle>
                            {book.summaryContents?.find(
                              (content) => content.language === currentLanguage
                            )?.contentType === "full_book"
                              ? "Chapters"
                              : "Chapter Summaries"}
                          </CardTitle>
                          <CardDescription>
                            {book.summaryContents?.find(
                              (content) => content.language === currentLanguage
                            )?.contentType === "full_book"
                              ? "Full chapter content"
                              : "Detailed chapter-by-chapter content"}{" "}
                            in{" "}
                            {languages.find((l) => l.code === currentLanguage)
                              ?.name || "selected"}{" "}
                            language
                          </CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          {book.summaryContents?.find(
                            (content) => content.language === currentLanguage
                          )?.isChaptersExpanded !== false ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </CardHeader>

                      {book.summaryContents?.find(
                        (content) => content.language === currentLanguage
                      )?.isChaptersExpanded !== false && (
                        <CardContent>
                          <ChapterEditor
                            currentChapters={
                              book.summaryContents?.find(
                                (content) =>
                                  content.language === currentLanguage
                              )?.chapters || []
                            }
                            languages={languages}
                            selectedLanguage={currentLanguage}
                            currentSummaryContent={book.summaryContents?.find(
                              (content) => content.language === currentLanguage
                            )}
                            onChaptersChange={(updatedChapters) => {
                              console.log(
                                "BookForm received updated chapters:",
                                updatedChapters
                              );
                              // Check if this is an empty array, which might indicate deletion
                              if (updatedChapters.length === 0) {
                                console.log(
                                  "Received empty chapters array, treating as a deletion"
                                );
                              }
                              updateSummaryContent(currentLanguage, {
                                chapters: updatedChapters,
                              });
                            }}
                          />
                        </CardContent>
                      )}
                    </Card>
                  </>
                )}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ai-context">
          <div className="space-y-6">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Bot className="h-6 w-6" />
                AI Context
              </h2>
              <p className="text-sm text-muted-foreground">
                Manage the AI context text used for RAG-based book chat. This text is chunked and embedded for semantic search.
              </p>
            </div>

            {book.summaryContents.length === 0 ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <Bot className="h-12 w-12 text-muted-foreground mx-auto" />
                    <div>
                      <h3 className="text-lg font-semibold mb-1">
                        No Languages Added
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Add a language in the "Summary Contents" tab first, then come back here to manage AI context.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {book.summaryContents
                    .slice()
                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                    .map((content) => {
                      const language = languages?.find(
                        (lang) => lang.code === content.language
                      );
                      if (!language) return null;
                      return (
                        <Button
                          key={content.language}
                          variant={content.language === currentLanguage ? "default" : "outline"}
                          size="sm"
                          className="flex items-center gap-2"
                          onClick={() => setCurrentLanguage(content.language)}
                        >
                          <Globe className="h-4 w-4" />
                          {language.name}
                          {content.aiContext ? (
                            <Badge variant="secondary" className="ml-1 text-xs">
                              {content.aiContext.split(/\s+/).filter(Boolean).length}w
                            </Badge>
                          ) : null}
                        </Button>
                      );
                    })}
                </div>

                {(() => {
                  const currentContent = book.summaryContents.find(
                    (c) => c.language === currentLanguage
                  );
                  if (!currentContent) return null;

                  const wordCount = currentContent.aiContext
                    ? currentContent.aiContext.split(/\s+/).filter(Boolean).length
                    : 0;

                  return (
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>
                              AI Context — {languages?.find((l) => l.code === currentLanguage)?.name}
                            </CardTitle>
                            <CardDescription>
                              This text will be chunked and embedded for AI-powered book chat.
                            </CardDescription>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (!currentContent.chapters?.length) {
                                toast({
                                  title: "No chapters found",
                                  description: "Add chapters in the Summary Contents tab first.",
                                  variant: "destructive",
                                });
                                return;
                              }

                              const sorted = currentContent.chapters
                                .slice()
                                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
                              const nonChapterLabels = /^(introduction|conclusion|tawpna|hmuhchhuahna)$/i;
                              const numberedTotal = sorted.filter((ch) => !nonChapterLabels.test(ch.label?.trim())).length;
                              let chapterIdx = 0;
                              const text = sorted
                                .map((ch) => {
                                  const isNumbered = !nonChapterLabels.test(ch.label?.trim());
                                  if (isNumbered) chapterIdx++;
                                  const label = ch.label || `Chapter ${chapterIdx} of ${numberedTotal}`;
                                  const heading = `## ${label}: ${ch.title}`;
                                  const startTimeLine =
                                    ch.startTime != null
                                      ? `[Audio: starts at ${(() => {
                                          const h = Math.floor(ch.startTime! / 3600);
                                          const m = Math.floor((ch.startTime! % 3600) / 60);
                                          const s = Math.floor(ch.startTime! % 60);
                                          const mm = String(m).padStart(2, "0");
                                          const ss = String(s).padStart(2, "0");
                                          return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
                                        })()}]\n\n`
                                      : "";
                                  return `${heading}\n${startTimeLine}\n${ch.content}`;
                                })
                                .join("\n\n");

                              const updatedContents = book.summaryContents.map((sc) =>
                                sc.language === currentLanguage
                                  ? { ...sc, aiContext: text }
                                  : sc
                              );
                              handleInputChange("summaryContents", updatedContents);

                              toast({
                                title: "AI context pre-filled",
                                description: `Pre-filled from ${currentContent.chapters.length} chapter(s).`,
                              });
                            }}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Pre-fill from Chapters
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Status Banner */}
                        {(() => {
                          const status = aiContextStatus[currentLanguage];
                          if (!status) return null;
                          const contextOutdated = status.context === "outdated";
                          const embOutdated = status.embeddings === "outdated";
                          const embMissing = status.embeddings === "missing";
                          const allGood = status.context === "up_to_date" && status.embeddings === "up_to_date";

                          if (allGood) {
                            return (
                              <div className="flex items-center gap-2 p-3 rounded-md bg-green-50 text-green-800 text-sm">
                                <CheckCircle className="h-4 w-4 shrink-0" />
                                <span>AI context and embeddings are up to date.</span>
                              </div>
                            );
                          }

                          return (
                            <div className="flex flex-col gap-2 p-3 rounded-md bg-yellow-50 text-yellow-800 text-sm">
                              {contextOutdated && (
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="h-4 w-4 shrink-0" />
                                  <span>Chapters have been updated since the AI context was generated.</span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="ml-auto h-7 text-xs border-yellow-300 text-yellow-800 hover:bg-yellow-100"
                                    onClick={() => {
                                      // Trigger Pre-fill from Chapters
                                      if (!currentContent.chapters?.length) return;
                                      const sorted = currentContent.chapters
                                        .slice()
                                        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
                                      const nonChapterLabels = /^(introduction|conclusion|tawpna|hmuhchhuahna)$/i;
                                      const numberedTotal = sorted.filter((ch) => !nonChapterLabels.test(ch.label?.trim())).length;
                                      let chapterIdx = 0;
                                      const text = sorted
                                        .map((ch) => {
                                          const isNumbered = !nonChapterLabels.test(ch.label?.trim());
                                          if (isNumbered) chapterIdx++;
                                          const label = ch.label || `Chapter ${chapterIdx} of ${numberedTotal}`;
                                          const heading = `## ${label}: ${ch.title}`;
                                          const startTimeLine =
                                            ch.startTime != null
                                              ? `[Audio: starts at ${(() => {
                                                  const h = Math.floor(ch.startTime! / 3600);
                                                  const m = Math.floor((ch.startTime! % 3600) / 60);
                                                  const s = Math.floor(ch.startTime! % 60);
                                                  const mm = String(m).padStart(2, "0");
                                                  const ss = String(s).padStart(2, "0");
                                                  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
                                                })()}]\n\n`
                                              : "";
                                          return `${heading}\n${startTimeLine}\n${ch.content}`;
                                        })
                                        .join("\n\n");
                                      const updatedContents = book.summaryContents.map((sc) =>
                                        sc.language === currentLanguage ? { ...sc, aiContext: text } : sc
                                      );
                                      handleInputChange("summaryContents", updatedContents);
                                      toast({ title: "AI context updated", description: "Remember to save the book, then regenerate embeddings." });
                                    }}
                                  >
                                    <FileText className="h-3 w-3 mr-1" />
                                    Update AI Context
                                  </Button>
                                </div>
                              )}
                              {(embOutdated || embMissing) && !contextOutdated && (
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="h-4 w-4 shrink-0" />
                                  <span>
                                    {embMissing
                                      ? "No embeddings generated yet."
                                      : "AI context has changed since embeddings were generated."}
                                  </span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="ml-auto h-7 text-xs border-yellow-300 text-yellow-800 hover:bg-yellow-100"
                                    disabled={isGeneratingEmbeddings || !currentContent.aiContext}
                                    onClick={() => handleGenerateEmbeddings(currentLanguage)}
                                  >
                                    <Sparkles className="h-3 w-3 mr-1" />
                                    {isGeneratingEmbeddings ? "Generating..." : "Generate Embeddings"}
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        <Textarea
                          value={currentContent.aiContext || ""}
                          onChange={(e) => {
                            const updatedContents = book.summaryContents.map((sc) =>
                              sc.language === currentLanguage
                                ? { ...sc, aiContext: e.target.value }
                                : sc
                            );
                            handleInputChange("summaryContents", updatedContents);
                          }}
                          placeholder="Enter or pre-fill the AI context text for this language..."
                          className="min-h-[400px] font-mono text-sm"
                        />
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{wordCount.toLocaleString()} words</span>
                            {embeddingCounts[currentLanguage] != null && (
                              <span>
                                {embeddingCounts[currentLanguage]} chunk(s) embedded
                              </span>
                            )}
                          </div>
                          <Button
                            variant="default"
                            size="sm"
                            disabled={isGeneratingEmbeddings || !currentContent.aiContext || isSaving}
                            onClick={() => handleGenerateEmbeddings(currentLanguage)}
                          >
                            {isGeneratingEmbeddings ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4 mr-2" />
                            )}
                            {isGeneratingEmbeddings ? "Generating..." : "Generate Embeddings"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={showDeleteConfirmation}
        onOpenChange={(open) => {
          console.log(
            "AlertDialog open state changed to:",
            open,
            "for book ID:",
            bookIdToDelete
          );
          setShowDeleteConfirmation(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Book Summary</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{book.title}"? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                console.log("Delete canceled for book ID:", bookIdToDelete);
                setShowDeleteConfirmation(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                console.log(
                  "Delete button clicked for book ID:",
                  bookIdToDelete
                );
                handleConfirmedDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default BookForm;
